/**
 * Sistema de persistência de seleção de variantes
 * Mantém as escolhas do usuário APENAS ao navegar entre produtos relacionados (product-group)
 * Se o acesso vier de qualquer outra fonte, as seleções são limpas
 * 
 * Debug: adicione ?debug_variants=1 na URL para ver logs detalhados
 */
(function () {
  if (window.__lagunaVariantPersistenceInit) return;
  window.__lagunaVariantPersistenceInit = true;

  const STORAGE_KEY = 'laguna_variant_selections';
  const STORAGE_EXPIRY = 30 * 60 * 1000; // 30 minutos
  const GROUP_NAV_PARAM = 'from_product_group';
  const DEBUG = new URLSearchParams(window.location.search).has('debug_variants');

  /**
   * Verifica se a navegação atual veio do product-group
   */
  function isFromProductGroup() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has(GROUP_NAV_PARAM);
  }

  /**
   * Limpa o parâmetro de navegação da URL (cosmético)
   */
  function cleanUrlParameter() {
    const url = new URL(window.location.href);
    if (url.searchParams.has(GROUP_NAV_PARAM)) {
      url.searchParams.delete(GROUP_NAV_PARAM);
      // Atualiza URL sem recarregar a página
      window.history.replaceState({}, '', url.toString());
      debugLog('Parâmetro de navegação removido da URL');
    }
  }

  /**
   * Log de debug condicional
   */
  function debugLog(...args) {
    if (DEBUG) {
      console.log('[Variant Persistence]', ...args);
    }
  }

  /**
   * Obtém as seleções salvas do sessionStorage
   */
  function getSavedSelections() {
    try {
      const data = sessionStorage.getItem(STORAGE_KEY);
      if (!data) return null;

      const parsed = JSON.parse(data);
      const now = Date.now();

      // Verifica se expirou
      if (parsed.expiry && now > parsed.expiry) {
        sessionStorage.removeItem(STORAGE_KEY);
        debugLog('Seleções expiraram e foram removidas');
        return null;
      }

      debugLog('Seleções recuperadas:', parsed.selections);
      return parsed.selections || null;
    } catch (e) {
      console.warn('Erro ao ler seleções de variantes:', e);
      return null;
    }
  }

  /**
   * Salva as seleções no sessionStorage
   */
  function saveSelections(selections) {
    try {
      const data = {
        selections: selections,
        expiry: Date.now() + STORAGE_EXPIRY,
        timestamp: Date.now()
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      debugLog('Seleções salvas:', selections);
    } catch (e) {
      console.warn('Erro ao salvar seleções de variantes:', e);
    }
  }

  /**
   * Extrai as opções selecionadas do variant-selects
   */
  function extractCurrentSelections(variantSelects) {
    if (!variantSelects) return {};

    const selections = {};

    // Procura por todos os inputs/selects de opções
    variantSelects.querySelectorAll('fieldset, .product-form__input').forEach((wrapper) => {
      // Tenta encontrar o nome da opção
      const legend = wrapper.querySelector('legend, .form__label');
      if (!legend) return;

      const optionName = legend.textContent.trim().replace(':', '').trim();
      if (!optionName) return;

      // Procura por select
      const select = wrapper.querySelector('select');
      if (select && select.selectedOptions && select.selectedOptions[0]) {
        const value = select.selectedOptions[0].value;
        if (value !== '') {
          selections[optionName] = value;
        }
        return;
      }

      // Procura por radio button
      const radio = wrapper.querySelector('input[type="radio"]:checked');
      if (radio && radio.value) {
        selections[optionName] = radio.value;
      }
    });

    return selections;
  }

  /**
   * Aplica as seleções salvas ao variant-selects atual
   */
  function applySelections(variantSelects, savedSelections) {
    if (!variantSelects || !savedSelections || Object.keys(savedSelections).length === 0) {
      return false;
    }

    // Adiciona atributo para feedback visual
    variantSelects.setAttribute('data-restoring-selection', 'true');

    let applied = false;
    const selectionsToApply = [];

    variantSelects.querySelectorAll('fieldset, .product-form__input').forEach((wrapper) => {
      const legend = wrapper.querySelector('legend, .form__label');
      if (!legend) return;

      const optionName = legend.textContent.trim().replace(':', '').trim();
      const savedValue = savedSelections[optionName];

      if (!savedValue) return;

      // Tenta aplicar em select
      const select = wrapper.querySelector('select');
      if (select) {
        const option = Array.from(select.options).find(
          (opt) => opt.value === savedValue
        );
        if (option && option.value !== '') {
          selectionsToApply.push(() => {
            select.value = savedValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            
            // Atualiza label do dropdown-swatch se existir
            const selectedValueSpan = wrapper.querySelector('[data-selected-value]');
            if (selectedValueSpan) {
              selectedValueSpan.textContent = savedValue;
            }
          });
          applied = true;
          return;
        }
      }

      // Tenta aplicar em radio button (incluindo variantes indisponíveis)
      const radio = wrapper.querySelector(`input[type="radio"][value="${CSS.escape(savedValue)}"]`);
      if (radio && !radio.disabled) {
        selectionsToApply.push(() => {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          // Simula click para garantir que os eventos sejam disparados
          radio.dispatchEvent(new Event('click', { bubbles: true }));
          
          // Atualiza label se existir
          const selectedValueSpan = wrapper.querySelector('[data-selected-value]');
          if (selectedValueSpan && radio.value) {
            selectedValueSpan.textContent = radio.value;
          }
        });
        applied = true;
      }
    });

    // Aplica as seleções com um pequeno delay entre cada uma
    selectionsToApply.forEach((applyFn, index) => {
      setTimeout(applyFn, index * 50);
    });

    // Remove o atributo de feedback visual após aplicar
    setTimeout(() => {
      variantSelects.removeAttribute('data-restoring-selection');
    }, selectionsToApply.length * 50 + 300);

    return applied;
  }

  /**
   * Monitora mudanças nas seleções e salva
   */
  function watchSelectionChanges(variantSelects) {
    if (!variantSelects) return;

    const saveCurrentState = () => {
      const selections = extractCurrentSelections(variantSelects);
      if (Object.keys(selections).length > 0) {
        saveSelections(selections);
        debugLog('Estado atual salvo automaticamente');
      }
    };

    // Monitora mudanças em selects e radios
    variantSelects.addEventListener('change', saveCurrentState, true);
    variantSelects.addEventListener('click', saveCurrentState, true);
  }

  /**
   * Adiciona parâmetros de variante aos links do product-group
   */
  function enhanceProductGroupLinks() {
    const savedSelections = getSavedSelections();
    
    document.querySelectorAll('.grouped-products-list a.select-grouped-item').forEach((link) => {
      if (!link.href) return;

      const url = new URL(link.href, window.location.origin);
      
      // Marca que a navegação vem do product-group
      url.searchParams.set(GROUP_NAV_PARAM, '1');
      
      // Adiciona as seleções como parâmetros de URL para hint (opcional)
      if (savedSelections && Object.keys(savedSelections).length > 0) {
        Object.entries(savedSelections).forEach(([key, value]) => {
          url.searchParams.set(`variant_${encodeURIComponent(key)}`, encodeURIComponent(value));
        });
      }

      link.href = url.toString();
      debugLog('Link do product-group atualizado:', link.href);
    });
  }

  /**
   * Inicializa o sistema
   */
  function init() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    // Verifica se a navegação veio do product-group
    const fromGroup = isFromProductGroup();
    debugLog('Navegação veio do product-group?', fromGroup);

    if (fromGroup) {
      // Limpa o parâmetro da URL (cosmético)
      cleanUrlParameter();

      // Tenta aplicar seleções salvas APENAS se veio do product-group
      const savedSelections = getSavedSelections();
      if (savedSelections && Object.keys(savedSelections).length > 0) {
        // Aguarda um momento para garantir que o DOM está pronto
        setTimeout(() => {
          const applied = applySelections(variantSelects, savedSelections);
          if (applied) {
            debugLog('Seleções aplicadas com sucesso no carregamento inicial');
          } else {
            debugLog('Nenhuma seleção pôde ser aplicada (valores não encontrados no produto atual)');
          }
        }, 100);
      }
    } else {
      // Se NÃO veio do product-group, limpa as seleções
      sessionStorage.removeItem(STORAGE_KEY);
      debugLog('Navegação não veio do product-group - seleções limpas');
    }

    // Começa a monitorar mudanças (sempre)
    watchSelectionChanges(variantSelects);

    // Atualiza links do product-group (sempre)
    enhanceProductGroupLinks();
  }

  /**
   * Re-inicializa quando o produto muda (product-info:loaded)
   * Isso ocorre em navegação AJAX dentro da mesma página
   */
  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target;
    if (!productInfo) return;

    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return;

    debugLog('Evento product-info:loaded disparado');

    // Em troca AJAX, sempre tenta aplicar (já estamos na página)
    setTimeout(() => {
      const savedSelections = getSavedSelections();
      if (savedSelections) {
        const applied = applySelections(variantSelects, savedSelections);
        debugLog('Produto trocado via AJAX - seleções aplicadas:', applied);
      }
      watchSelectionChanges(variantSelects);
      enhanceProductGroupLinks();
    }, 150);
  });

  // Inicializa quando o DOM estiver pronto
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Limpa seleções ao sair da página de produto
  window.addEventListener('beforeunload', () => {
    const currentPath = window.location.pathname;
    if (!currentPath.includes('/products/')) {
      sessionStorage.removeItem(STORAGE_KEY);
      debugLog('Seleções limpas ao sair da página de produto');
    }
  });

  // Adiciona listener aos links do product-group para marcar navegação
  document.addEventListener('click', (e) => {
    const link = e.target.closest('.grouped-products-list a.select-grouped-item');
    if (link) {
      debugLog('Clique em link do product-group detectado');
      // O link já tem o parâmetro adicionado pela função enhanceProductGroupLinks
    }
  }, true);

  // API pública para limpar seleções manualmente
  window.LagunaVariantPersistence = {
    clearSelections: function() {
      sessionStorage.removeItem(STORAGE_KEY);
      debugLog('Seleções limpas manualmente via API');
      console.log('Seleções de variantes limpas');
    },
    getSelections: function() {
      return getSavedSelections();
    },
    enableDebug: function() {
      window.location.search = '?debug_variants=1';
    },
    isFromProductGroup: function() {
      return isFromProductGroup();
    }
  };

  debugLog('Sistema de persistência de variantes inicializado');
})();
